import { InboundMessageType } from '../../../types/InboundMessageType';
import { type InboundMessagePayload } from '../../../types/InboundMessagePayload';
import { FieldName, type TCardFieldName } from '../../../types/CardFieldName';
import { ErrorMessage } from '../../../types/ErrorMessage';
import { type CardFormAPI } from '../../CardForm/types';
import { type MessageResponse } from '../../../utils/messages/types';

export type MessageHandler = () => Promise<MessageResponse | undefined> | (MessageResponse | undefined);
export type CompleteMessageHandlersMap = Record<InboundMessageType, MessageHandler>;
export type PartialMessageHandlersMap = Partial<CompleteMessageHandlersMap>;

export const messagesRequiringValidForm: InboundMessageType[] = [
  InboundMessageType.GetPublicData,
  InboundMessageType.Tokenize,
];

export async function processInboundMessage(
  type: InboundMessageType,
  cardFormAPI: CardFormAPI,
  handlers: PartialMessageHandlersMap,
): Promise<MessageResponse | undefined> {
  if (messagesRequiringValidForm.includes(type) && !cardFormAPI.isValid()) {
    const invalidFields = cardFormAPI.validate().invalidFields;
    return {
      error: {
        message: ErrorMessage.InvalidCard,
        details: `Invalid fields: ${invalidFields?.join(', ')}`,
      },
    };
  }

  const handler = handlers[type];
  if (!handler) {
    return undefined;
  }

  try {
    return await handler();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { error: { message } };
  }
}

export const createValidateHandler =
  (cardFormAPI: CardFormAPI): MessageHandler =>
  () =>
    cardFormAPI.validate();

export const createFocusInvalidFieldHandler =
  (cardFormAPI: CardFormAPI, data: InboundMessagePayload): MessageHandler =>
  () => {
    const fieldName = data.fieldName as TCardFieldName | undefined;
    if (!fieldName || !(Object.values(FieldName) as string[]).includes(fieldName)) {
      return {
        error: {
          message: ErrorMessage.InvalidParams,
          details: `Invalid field name provided: ${fieldName}. One of the following field names expected: ${Object.values(
            FieldName,
          ).join(', ')}`,
        },
      };
    }
    cardFormAPI.focusInvalidField(fieldName);
    return undefined;
  };
